import type { ArenaBounds } from "./config";

export interface Point {
  x: number;
  y: number;
}

export interface ArenaProjection {
  world: ArenaBounds;
  available: ArenaBounds;
  screen: ArenaBounds;
  scale: number;
}

export function createArenaProjection(
  world: ArenaBounds,
  available: ArenaBounds,
): ArenaProjection {
  const scale = Math.min(
    available.width / world.width,
    available.height / world.height,
  );
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const screenWidth = world.width * safeScale;
  const screenHeight = world.height * safeScale;

  return {
    world: { ...world },
    available: { ...available },
    screen: {
      x: available.x + (available.width - screenWidth) / 2,
      y: available.y + (available.height - screenHeight) / 2,
      width: screenWidth,
      height: screenHeight,
    },
    scale: safeScale,
  };
}

export function worldToScreenPoint(
  projection: ArenaProjection,
  point: Point,
): Point {
  return {
    x: projection.screen.x + (point.x - projection.world.x) * projection.scale,
    y: projection.screen.y + (point.y - projection.world.y) * projection.scale,
  };
}

export function screenToWorldPoint(
  projection: ArenaProjection,
  point: Point,
): Point {
  return {
    x: projection.world.x + (point.x - projection.screen.x) / projection.scale,
    y: projection.world.y + (point.y - projection.screen.y) / projection.scale,
  };
}

export function worldLengthToScreen(
  projection: ArenaProjection,
  value: number,
): number {
  return value * projection.scale;
}

export function screenLengthToWorld(
  projection: ArenaProjection,
  value: number,
): number {
  return value / projection.scale;
}
